import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sparkles, Globe, ChevronDown, Phone } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { trackWhatsAppClick, trackConsultationCTAClick, trackCallClick, trackEvent } from '../utils/analytics';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current pointer-events-none"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [horoOpen, setHoroOpen] = useState(false);
  const location = useLocation();
  const { lang, toggleLang, t } = useLang();
  const horoRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClickOutside = (e) => {
      if (horoRef.current && !horoRef.current.contains(e.target)) setHoroOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // ज्योतिषीय उपकरण group — all astrology tools live here (per Feb 2026 nav spec)
  const horoscopeItems = [
    { name: lang === 'hi' ? 'पंचांग' : 'Daily Panchang', path: '/daily-panchang', desc: lang === 'hi' ? 'तिथि, नक्षत्र, राहु काल' : 'Tithi, Nakshatra, Rahu Kaal' },
    { name: lang === 'hi' ? 'राशिफल' : 'Daily Rashifal', path: '/daily-rashifal', desc: lang === 'hi' ? '12 राशि • प्रतिदिन' : '12 signs • daily' },
    { name: lang === 'hi' ? 'कुंडली' : 'Kundli', path: '/kundli', desc: lang === 'hi' ? 'Free Birth Chart' : 'Free Birth Chart' },
    { name: lang === 'hi' ? 'गुण मिलान' : 'Gun Milan', path: '/gun-milan', desc: lang === 'hi' ? '36-गुण विवाह मिलान' : '36-Guna matching' },
    { name: lang === 'hi' ? 'अंक शास्त्र' : 'Numerology', path: '/numerology', desc: lang === 'hi' ? 'Lucky Numbers' : 'Lucky Numbers' },
    { name: lang === 'hi' ? 'टैरो' : 'Daily Tarot', path: '/daily-tarot', desc: lang === 'hi' ? '3-कार्ड स्प्रेड' : '3-card spread' },
    { name: lang === 'hi' ? 'वास्तु' : 'Vastu', path: '/#vastu', desc: lang === 'hi' ? 'घर/Office परामर्श' : 'Home/Office guidance' },
    { name: lang === 'hi' ? 'नवग्रह उपाय' : 'Nav Graha Upay', path: '/upay', desc: lang === 'hi' ? 'मन्त्र, रुद्राक्ष, रत्न' : 'Mantra, Rudraksha, Gemstones' },
  ];

  // Top-level navigation — Feb 2026 spec (EXACT order from user):
  // होम | सेवाएं | पूछो गुरु जी | ज्योतिषीय उपकरण (dropdown) | ब्लॉग | हमारे बारे में | परिचय | सम्पर्क
  // "ज्योतिषीय उपकरण" is rendered as a separate dropdown in the JSX below.
  const topLinks = [
    { name: lang === 'hi' ? 'होम' : 'Home', path: '/' },
    { name: lang === 'hi' ? 'सेवाएं' : 'Services', path: '/#services' },
    { name: lang === 'hi' ? 'पूछो गुरुजी · ₹99' : 'Pucho Guruji · ₹99', path: '/poocho-guruji', highlight: true },
    { name: lang === 'hi' ? 'ब्लॉग' : 'Blog', path: '/blog' },
    { name: lang === 'hi' ? 'हमारे बारे में' : 'About', path: '/about' },
    { name: lang === 'hi' ? 'परिचय' : 'Bio', path: '/experts/vivek-virat-mudgal' },
    { name: lang === 'hi' ? 'सम्पर्क' : 'Contact', path: '/#contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.replace('/#', ''));
  };
  const isHoroActive = () => horoscopeItems.some((i) => isActive(i.path));

  const handleNavClick = (e, path) => {
    if (path.includes('#')) {
      const sectionId = path.split('#')[1];
      if (location.pathname === '/') {
        e.preventDefault();
        const el = document.getElementById(sectionId);
        if (el) {
          el.scrollIntoView({ behavior: 'instant', block: 'start' });
          window.scrollBy(0, -80);
        }
      }
      setIsOpen(false);
      setHoroOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a0710]/75 backdrop-blur-2xl border-b border-gold/10">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0" data-testid="logo-link">
            <img
              src="/brand/va-icon-transparent-512.png"
              alt="ViratAstro VA Monogram"
              width="44" height="44"
              className="w-10 h-10 sm:w-11 sm:h-11 shrink-0 drop-shadow-[0_0_8px_rgba(212,175,55,0.45)]"
              loading="eager"
              decoding="async"
              data-testid="header-logo-img"
            />
            {/* Hide brand-text on ultra-narrow viewports (Galaxy Fold inner, 280-359px) so the
                right-side CTAs and Menu icon never get cut off. */}
            <div className="hidden xs:block min-w-0">
              {/* Feb 12, 2026 SEO Fix — brand element was <h1> on every page,
                  causing "multiple H1" audit finding on every route. Downgraded
                  to <span> — the actual page primary H1 belongs to the content. */}
              <span className="font-heading text-lg sm:text-xl font-medium text-text-main tracking-wide truncate">ViratAstro</span>
              <p className="hidden sm:block text-[10px] text-gold/60 tracking-[0.25em] uppercase font-body">Astrology &amp; Vastu</p>
            </div>
          </Link>

          {/* Desktop Navigation — Feb 2026 spec order:
              होम | सेवाएं | पूछो गुरुजी | ज्योतिषीय उपकरण | ब्लॉग | हमारे बारे में | परिचय | सम्पर्क */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Home */}
            <Link to="/" data-testid="nav-home"
              className={`text-sm font-medium transition-colors font-body ${isActive('/') ? 'text-gold' : 'text-text-muted hover:text-gold/80'}`}>
              {t('nav.home')}
              {isActive('/') && <span className="block h-[2px] bg-gradient-to-r from-gold to-purple-light rounded-full mt-0.5" />}
            </Link>

            {/* सेवाएं + पूछो गुरुजी (highlighted) — items 2-3 */}
            {topLinks.slice(1, 3).map((link) => (
              <Link key={link.path} to={link.path} data-testid={`nav-${link.name}`}
                onClick={(e) => handleNavClick(e, link.path)}
                className={`text-sm font-medium transition-colors font-body ${link.highlight ? 'px-3 py-1 rounded-full bg-gold/10 border border-gold/40 text-gold hover:bg-gold/20' : (isActive(link.path) ? 'text-gold' : 'text-text-muted hover:text-gold/80')}`}>
                {link.name}
                {!link.highlight && isActive(link.path) && <span className="block h-[2px] bg-gradient-to-r from-gold to-purple-light rounded-full mt-0.5" />}
              </Link>
            ))}

            {/* ज्योतिषीय उपकरण Dropdown — item 4 */}
            <div className="relative" ref={horoRef}>
              <button onClick={() => setHoroOpen(!horoOpen)} data-testid="nav-horoscope-dropdown"
                className={`flex items-center gap-1 text-sm font-medium transition-colors font-body ${isHoroActive() ? 'text-gold' : 'text-text-muted hover:text-gold/80'}`}>
                {lang === 'hi' ? 'ज्योतिषीय उपकरण' : 'Astrology Tools'}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${horoOpen ? 'rotate-180' : ''}`} />
                {isHoroActive() && <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-gradient-to-r from-gold to-purple-light rounded-full" />}
              </button>
              {horoOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[440px] glass-card border border-gold/25 rounded-2xl shadow-2xl shadow-purple-950/50 p-3 grid grid-cols-2 gap-1 z-50"
                  data-testid="horoscope-dropdown-menu">
                  {horoscopeItems.map((item) => (
                    <Link key={item.path} to={item.path} onClick={(e) => { handleNavClick(e, item.path); setHoroOpen(false); }}
                      className={`flex flex-col px-3 py-2.5 rounded-xl transition-all hover:bg-gold/10 ${isActive(item.path) ? 'bg-gold/10' : ''}`}
                      data-testid={`horo-item-${item.path.replace(/[/#]/g, '')}`}>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${isActive(item.path) ? 'text-gold' : 'text-text-main'}`}>{item.name}</span>
                        {item.badge && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gold text-purple-950 font-bold">{item.badge}</span>}
                      </div>
                      <span className="text-[11px] text-text-muted mt-0.5">{item.desc}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Remaining top-level links (items 5-8) */}
            {topLinks.slice(3).map((link) => (
              <Link key={link.path} to={link.path} data-testid={`nav-${link.name}`}
                onClick={(e) => handleNavClick(e, link.path)}
                className={`text-sm font-medium transition-colors font-body ${isActive(link.path) ? 'text-gold' : 'text-text-muted hover:text-gold/80'}`}>
                {link.name}
                {isActive(link.path) && <span className="block h-[2px] bg-gradient-to-r from-gold to-purple-light rounded-full mt-0.5" />}
              </Link>
            ))}

            {/* Right-side controls */}
            <div className="flex items-center gap-3 ml-2 border-l border-gold/15 pl-5">
              <button onClick={toggleLang} data-testid="lang-toggle-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gold/20 bg-white/5 hover:bg-gold/10 transition-colors text-sm font-medium text-gold"
                title={lang === 'hi' ? 'Switch to English' : 'हिंदी में बदलें'}>
                <Globe className="w-3.5 h-3.5" />
                {lang === 'hi' ? 'EN' : 'हि'}
              </button>
              <a href="tel:+919311369729"
                onClick={() => trackCallClick('header')}
                className="w-9 h-9 rounded-full bg-purple-700 hover:bg-purple-800 flex items-center justify-center text-white transition-colors"
                data-testid="header-call-btn" title="Call Guruji">
                <Phone className="w-4 h-4" />
              </a>
              <a href="https://wa.me/919311369729" target="_blank" rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('header')}
                className="w-9 h-9 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center text-white transition-colors"
                data-testid="header-whatsapp-btn" title="WhatsApp">
                <WhatsAppIcon />
              </a>
              <a href="/#book-consultation"
                onClick={() => trackConsultationCTAClick('header')}
                className="btn-gold text-sm flex items-center gap-2 shrink-0" data-testid="header-consult-btn">
                <Sparkles className="w-4 h-4" /> {t('nav.consultNow')}
              </a>
            </div>
          </div>

          {/* Mobile Header — Simplified: WhatsApp + Book + Menu only.
              Reasons: no dedicated call-handling staff (missed calls hurt UX) and
              WhatsApp is the primary communication channel. Language toggle moved
              inside the drawer to free up edge space for a fully-visible Menu icon. */}
          <div className="lg:hidden flex items-center gap-2 shrink-0 mobile-header-right">
            <a href="https://wa.me/919311369729" target="_blank" rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('mobile_header')}
              className="w-10 h-10 rounded-full bg-green-600 active:bg-green-700 flex items-center justify-center text-white shrink-0"
              data-testid="mobile-whatsapp-btn" aria-label="WhatsApp Acharya ji">
              <WhatsAppIcon />
            </a>
            <a href="/#book-consultation"
              onClick={() => trackConsultationCTAClick('mobile_header')}
              className="px-3 h-10 inline-flex items-center rounded-full bg-gradient-to-r from-gold to-amber-500 text-purple-950 text-xs font-bold shrink-0"
              data-testid="mobile-consult-btn">
              {lang === 'hi' ? 'परामर्श' : 'Book'}
            </a>
            <button
              onClick={() => {
                const willOpen = !isOpen;
                setIsOpen(willOpen);
                if (willOpen) trackEvent('mobile_menu_open', { cta_source: 'mobile_header' });
              }}
              className="w-11 h-11 inline-flex items-center justify-center rounded-full border border-gold/30 bg-white/5 hover:bg-gold/10 active:bg-gold/15 text-gold shrink-0"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gold/10 bg-[#1a0710]/95 backdrop-blur-2xl max-h-[80vh] overflow-y-auto" data-testid="mobile-drawer">
            {/* Language toggle (moved from header bar to free up edge space) */}
            <div className="flex justify-end pb-3 mb-2 border-b border-gold/10">
              <button onClick={toggleLang} data-testid="lang-toggle-mobile"
                className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-gold/25 bg-white/5 hover:bg-gold/10 active:bg-gold/15 text-xs font-medium text-gold"
                aria-label={lang === 'hi' ? 'Switch to English' : 'हिंदी में बदलें'}>
                <Globe className="w-3.5 h-3.5" />
                {lang === 'hi' ? 'English' : 'हिंदी'}
              </button>
            </div>

            <Link to="/" data-testid="mobile-nav-home"
              className={`block py-3 text-sm font-medium ${isActive('/') ? 'text-gold' : 'text-text-muted'}`}
              onClick={() => setIsOpen(false)}>{t('nav.home')}</Link>

            {/* Horoscope group */}
            <div className="py-2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold/60 font-semibold mb-2">
                {lang === 'hi' ? 'ज्योतिषीय उपकरण' : 'Astrology Tools'}
              </p>
              {horoscopeItems.map((item) => (
                <Link key={item.path} to={item.path} data-testid={`mobile-nav-${item.path}`}
                  className={`flex items-center justify-between py-2.5 pl-3 text-sm ${isActive(item.path) ? 'text-gold' : 'text-text-muted'}`}
                  onClick={(e) => { handleNavClick(e, item.path); setIsOpen(false); }}>
                  <span>{item.name}</span>
                  {item.badge && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gold text-purple-950 font-bold">{item.badge}</span>}
                </Link>
              ))}
            </div>

            {/* Other links */}
            {topLinks.slice(1).map((link) => (
              <Link key={link.path} to={link.path} data-testid={`mobile-nav-${link.name}`}
                className={`block py-3 text-sm font-medium ${isActive(link.path) ? 'text-gold' : 'text-text-muted'}`}
                onClick={(e) => handleNavClick(e, link.path)}>
                {link.name}
              </Link>
            ))}

            <div className="flex gap-3 mt-4">
              <a href="https://wa.me/919311369729" target="_blank" rel="noopener noreferrer"
                onClick={() => { trackWhatsAppClick('mobile_drawer'); setIsOpen(false); }}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-full text-sm font-semibold">
                <WhatsAppIcon /> WhatsApp
              </a>
              <a href="/#book-consultation" className="btn-gold text-sm"
                onClick={() => { trackConsultationCTAClick('mobile_drawer'); setIsOpen(false); }}>
                {t('nav.consultNow')}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
